import type { ContentBlockType, ContentVocabularyItemDto } from '../services/contentService';
import {
  normalizeStickyAudioPhraseKey,
  type StickyAudioMap,
  type StickyAudioMapEntry
} from './stickyAudio';

export const WORD_HELP_OPEN_EVENT = 'persianist:word-help-open';

export type WordHelpOpenDetail = {
  vocabId: number;
  audioUrl: string;
  rect: { top: number; left: number; bottom: number; right: number; width: number; height: number };
  anchor?: HTMLElement;
};

type TextBlockLike = {
  type?: ContentBlockType | number;
  textContent?: string | null;
  textContentFa?: string | null;
  textFormatting?: string | null;
  textFormattingFa?: string | null;
  vocabularyItems?: ContentVocabularyItemDto[];
};

function formattedPlain(json?: string | null): string {
  if (!json) return '';
  try {
    const parsed = JSON.parse(json) as { formattedContent?: string };
    const html = parsed.formattedContent ?? '';
    return html.replace(/<[^>]+>/g, ' ');
  } catch {
    return '';
  }
}

function vocabMarkId(item: ContentVocabularyItemDto): number {
  if (item.id != null && item.id > 0) return item.id;
  const key = item.entryKey || item.persian;
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  const abs = Math.abs(hash);
  return abs === 0 ? -1 : -abs;
}

export function vocabularyToStickyMap(items: ContentVocabularyItemDto[]): StickyAudioMap {
  const map: StickyAudioMap = {};
  const ordered = [...items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  for (const item of ordered) {
    const phrase = item.persian?.trim();
    if (!phrase) continue;
    const phraseKey = normalizeStickyAudioPhraseKey(phrase);
    if (!phraseKey || map[phraseKey]) continue;
    const entry: StickyAudioMapEntry = {
      phrase,
      phraseKey,
      audioUrl: item.audioUrl ?? '',
      vocabId: vocabMarkId(item),
      english: item.english ?? '',
      partOfSpeech: item.partOfSpeech
    };
    if (item.audioFileName) entry.audioFileName = item.audioFileName;
    if (item.phonetic) entry.phonetic = item.phonetic;
    if (item.example) entry.example = item.example;
    if (item.imageUrl) entry.imageUrl = item.imageUrl;
    if (item.entryKey) entry.entryKey = item.entryKey;
    map[phraseKey] = entry;
  }
  return map;
}

export function collectVocabularyItems(blocks: TextBlockLike[]): ContentVocabularyItemDto[] {
  const items: ContentVocabularyItemDto[] = [];
  for (const block of blocks) {
    if (block.vocabularyItems?.length) items.push(...block.vocabularyItems);
  }
  return items;
}

/** Plain text of lesson/book text blocks, sent to the all-words lookup. */
export function collectWordHelpText(blocks: TextBlockLike[]): string {
  const parts: string[] = [];
  for (const block of blocks) {
    const type = block.type;
    if (type !== 0 && type !== 6) continue;
    if (block.textContent) parts.push(block.textContent);
    if (block.textContentFa) parts.push(block.textContentFa);
    const en = formattedPlain(block.textFormatting);
    const fa = formattedPlain(block.textFormattingFa);
    if (en) parts.push(en);
    if (fa) parts.push(fa);
  }
  return parts.join('\n');
}

/**
 * All-words is this lesson plus the catalog. Lesson phrases stay even when the
 * catalog hit uses a different surface form (باغ vs باغِ). Catalog wins on the same key.
 */
export function mergeWordHelpMaps(localMap: StickyAudioMap, allMap: StickyAudioMap): StickyAudioMap {
  return { ...localMap, ...allMap };
}

export function mergeVocabularyItems(
  primary: ContentVocabularyItemDto[],
  extra: ContentVocabularyItemDto[]
): ContentVocabularyItemDto[] {
  const seen = new Set<number>();
  const merged: ContentVocabularyItemDto[] = [];
  for (const item of [...primary, ...extra]) {
    if (item.id != null) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
    }
    merged.push(item);
  }
  return merged;
}
